import { useSessionStorage } from '../hooks/useSessionStorage';
import { useCallback } from 'react';
import { useProfilesLocalStorage } from '../ProfilesLocalStorageContext';
import { isProfileIconImage } from '../typeGuards';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';
import type { ContentLanguage, Gender } from '@yleisradio/tunnus-types';

const LOGIN_API_ERROR_SESSION_STORAGE_KEY = 'login_api_has_errored';

const useLoginApiError = () => {
  const { setUser } = useAuthContext();

  const [loginApiHasErrored, setLoginApiHasErrored] =
    useSessionStorage<boolean>(LOGIN_API_ERROR_SESSION_STORAGE_KEY, false);
  const { profiles } = useProfilesLocalStorage();

  const flagLoginApiErrorForSession = useCallback(() => {
    setLoginApiHasErrored(true);
  }, [setLoginApiHasErrored]);

  const recoverFromLoginApiServerError5xx = useCallback(
    (sessionCookie: string | undefined) => {
      /* We might have a session cookie from request, if login-api errored via our Express application server.
       * We assume session is still valid and try to set login.user state from localStorage.
       */
      const profile = profiles.find(p => p.cookie === sessionCookie);
      if (profile) {
        setUser({
          age: profile.age,
          alternativeLanguages: [],
          consent: profile.consent,
          deleteHistoryRequestedAt: null,
          displayName: profile.displayName,
          hasPassword: false,
          initials: isProfileIconImage(profile.icon) ? '' : profile.icon.value,
          isSubAccount: profile.parentId !== null,
          latestTermsOfServiceAccepted: true,
          nick: null,
          nickUpdatedAt: null,
          passwordUpdatedAt: null,
          personalization: profile.consent?.values.personalization ?? false,
          primaryLanguage: 'fi' as ContentLanguage,
          sex: 'UNDEFINED' as Gender,
          termsOfServiceExtensions: [],
          transientId: '',
          userId: profile.userId,
          username: profile.displayName || profile.username || '',
        });
      }
    },

    [profiles, setUser]
  );

  return {
    loginApiHasErrored,
    flagLoginApiErrorForSession,
    recoverFromLoginApiServerError5xx,
  };
};

export default useLoginApiError;
